import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { rpc, fmtTime, spDay, todaySP } from "@/lib/booking";
import { label } from "./ui";

const WEEK = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const shift = (month, d) => {
    const [y, m] = month.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1 + d, 1)).toISOString().slice(0, 7);
};
function monthDays(month) {
    const [y, m] = month.split("-").map(Number);
    const total = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return {
        first: new Date(Date.UTC(y, m - 1, 1)).getUTCDay(),
        days: Array.from({ length: total }, (_, i) => `${month}-${String(i + 1).padStart(2, "0")}`),
    };
}
const monthName = (month) =>
    new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${month}-15T12:00:00Z`));

function useCalendar(tourId, month, today) {
    const [data, setData] = useState({ slots: [], blocked: [], state: "loading" });
    useEffect(() => {
        let live = true;
        const { days } = monthDays(month);
        const from = days[0] < today ? today : days[0];
        const to = days[days.length - 1];
        setData((d) => ({ ...d, state: "loading" }));
        Promise.all([
            rpc("booking_calendar", { p_tour: tourId, p_from: from, p_to: to }),
            rpc("booking_blocked_days", { p_tour: tourId, p_from: from, p_to: to }),
        ])
            .then(([s, b]) => live && setData({
                slots: s || [],
                blocked: (b || []).map((x) => (typeof x === "string" ? x : Object.values(x)[0])),
                state: "ok",
            }))
            .catch(() => live && setData({ slots: [], blocked: [], state: "error" }));
        return () => { live = false; };
    }, [tourId, month, today]);
    return data;
}

function TimeList({ slots, guests, value, onChange }) {
    const groups = [["manha", "Manhã"], ["tarde", "Tarde"]];
    return groups.map(([key, title]) => {
        const list = slots.filter((s) => s.period === key);
        if (!list.length) return null;
        return (
            <div key={key} className="mt-4">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-dim">{title}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                    {list.map((s) => {
                        const off = s.available < guests;
                        const on = value?.slot_id === s.slot_id;
                        return (
                            <button key={s.slot_id} type="button" disabled={off} onClick={() => onChange(s)}
                                data-testid={`slot-${s.slot_id}`}
                                className={`rounded-2xl border px-4 py-2.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${on ? "border-neon-cyan bg-neon-cyan/10 text-neon-cyan" : "border-ink/20 hover:border-neon-cyan/60"}`}>
                                <span className="block font-display text-sm font-bold">{fmtTime(s.starts_at)}</span>
                                <span className="block text-[11px] text-dim">{s.available > 0 ? `${s.available} vaga(s)` : "Esgotado"}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    });
}

export function AvailabilityCalendar({ tourId, initialDate, value, onChange, guests }) {
    const today = todaySP();
    const [month, setMonth] = useState((initialDate && initialDate >= today ? initialDate : today).slice(0, 7));
    const [day, setDay] = useState(initialDate && initialDate >= today ? initialDate : "");
    const { slots, blocked, state } = useCalendar(tourId, month, today);
    const byDay = useMemo(() => slots.reduce((acc, s) => {
        (acc[spDay(s.starts_at)] ||= []).push(s);
        return acc;
    }, {}), [slots]);
    const { first, days } = monthDays(month);
    const daySlots = byDay[day] || [];

    return (
        <div data-testid="availability-calendar">
            <span className={label}>Data e horário</span>
            <div className="rounded-3xl border border-ink/15 p-4">
                <div className="flex items-center justify-between">
                    <button type="button" aria-label="Mês anterior" data-testid="calendar-prev" disabled={month <= today.slice(0, 7)}
                        onClick={() => setMonth(shift(month, -1))} className="rounded-full p-2 text-neon-cyan disabled:opacity-30"><ChevronLeft className="h-5 w-5" /></button>
                    <p className="font-display text-sm font-bold capitalize" data-testid="calendar-month">{monthName(month)}</p>
                    <button type="button" aria-label="Próximo mês" data-testid="calendar-next" disabled={month >= shift(today.slice(0, 7), 6)}
                        onClick={() => setMonth(shift(month, 1))} className="rounded-full p-2 text-neon-cyan disabled:opacity-30"><ChevronRight className="h-5 w-5" /></button>
                </div>
                <div className="mt-3 grid grid-cols-7 gap-1 text-center">
                    {WEEK.map((w) => <span key={w} className="py-1 font-mono2 text-[9px] uppercase text-dim">{w}</span>)}
                    {Array.from({ length: first }, (_, i) => <span key={`e${i}`} />)}
                    {days.map((d) => {
                        const list = byDay[d] || [];
                        const isBlocked = blocked.includes(d);
                        const free = list.some((s) => s.available >= guests);
                        const disabled = d < today || isBlocked || !free;
                        return (
                            <button key={d} type="button" disabled={disabled} data-testid={`calendar-day-${d}`}
                                onClick={() => { setDay(d); onChange(null); }}
                                title={isBlocked ? "Data indisponível" : list.length && !free ? "Esgotado" : undefined}
                                className={`h-10 rounded-xl text-sm transition-colors sm:h-11 ${day === d ? "bg-neon-cyan font-bold text-ocean" : free && !disabled ? "border border-neon-cyan/40 text-ink hover:bg-neon-cyan/10" : "text-dim/40"} ${isBlocked || (list.length && !free) ? "line-through" : ""}`}>
                                {Number(d.slice(8))}
                            </button>
                        );
                    })}
                </div>
                {state === "loading" && <p className="mt-3 text-xs text-dim">Consultando agenda…</p>}
                {state === "error" && <p role="alert" className="mt-3 text-xs text-neon-coral" data-testid="calendar-error">Agenda indisponível no momento. Solicite pelo WhatsApp.</p>}
                {state === "ok" && !slots.length && <p className="mt-3 text-xs text-dim" data-testid="calendar-empty">Nenhum horário disponível neste mês.</p>}
            </div>
            {day && <TimeList slots={daySlots} guests={guests} value={value} onChange={onChange} />}
        </div>
    );
}
